import React, { useCallback, useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import _ from 'lodash';
import { Text as DangerText } from '@this/shared/ui/data_displays/typography';
import { Datepicker } from '@this/shared/ui/inputs/datepicker/datepicker';
import { Button } from '@this/src/components/shared/ui/inputs/button';
import { Link } from '@this/shared/ui/navigations/link';
import { Fetcher, HTTPError, isAiTravel, isMynavi } from '@this/src/util';

import type Trip from '@this/domain/trip/trip2';
import type Order from '@this/domain/order2';
import type OrderItem from '@this/domain/order_item2';
import TransportElement from '@this/domain/transport_element2';
import HotelElement from '@this/domain/hotel_element2';
import CancelChargeElement from '@this/domain/cancel_charge_element2';
import SupportFeeElement from '@this/domain/support_fee_element/support_fee_element2';
import ShippingElement from '@this/domain/shipping_element/shipping_element2';
import WifiElement from '@this/domain/wifi_element/wifi_element2';
import KitElement from '@this/domain/kit_element/kit_element2';
import RoomElement from '@this/domain/room_element/room_element2';
import TravelAuthorizationElement from '@this/domain/travel_authorization_element2';
import InsuranceElement from '@this/src/domain/insurance_element2';
import ChangeFeeElement from '@this/src/domain/change_fee_element2';
import type { CabinOption } from '@this/domain/cabin_option';
import type { IndividualTargetSuppliedItem } from '@this/domain/individual_target_supplied_item';
import type { IndividualDomesticAirSuppliedItem } from '@this/domain/individual_domestic_air_supplied_item';
import {
  ForeignFlightTicketIssuer,
  ForeignFlightTicketIssuerKeys
} from '@this/domain/order_item/foreign_flight_ticket_issuer';
import { TAX_TYPE_ID } from '@this/domain/tax_type';
import Text from '@this/shared/text/text';
import moment from 'moment';
import TransportElementForm from './transport_element_form/transport_element_form';
import HotelElementForm from './hotel_element_form/hotel_element_form';
import CancelElementForm from './cancel_element_form/cancel_element_form';
import SupportFeeElementForm from './support_fee_element_form/support_fee_element_form';
import ShippingElementForm from './shipping_element_form/shipping_element_form';
import WifiElementForm from './wifi_element_form/wifi_element_form';
import KitElementForm from './kit_element_form/kit_element_form';
import RoomElementForm from './room_element_form/room_element_form';
import TravelAuthorizationElementForm from './travel_authorization_element_form/travel_authorization_element_form';
import InsuranceElementForm from './insurance_element_form/insurance_element_form';
import ChangeFeeElementForm from './change_fee_element_form/change_fee_element_form';
import type { TimeZone } from '../price_change_form';
import type {
  PnrRetrieveAllResponseJson,
  PnrRetrievePriceResponseJson,
  PnrRetrieveFlightResponseJson
} from './price_change_form_item';

export interface OrderItemEditFormProps {
  order: Order;
  orderItem: OrderItem;
  selectedTrip: Trip;
  serviceId: number;
  cabinOptions: CabinOption[];
  individualTargetSuppliedItems: IndividualTargetSuppliedItem[];
  individualDomesticAirSuppliedItems: IndividualDomesticAirSuppliedItem[];
  handleRemoveElement: (orderItem: OrderItem, i: number) => void;
  updateIsAirNumbersMissing: (orderItem: OrderItem) => void;
  hotelElementProviderOptions: { [key: string]: string };
  carProviderOptions?: Array<[string, string]>;
  carTypeOptions?: Array<[string, string]>;
  shippingCategoryOptions?: { [key: string]: string };
  wifiProviderOptions?: { [key: string]: string };
  updateForeignAirTransportElements: (
    orderItem: OrderItem,
    retrieveResult: PnrRetrieveFlightResponseJson[]
  ) => void;
  updatePriceDetailsFromPnr: (orderItem: OrderItem, retrieveResult: PnrRetrievePriceResponseJson[]) => void;
  timeZones: TimeZone[];
}

const OrderItemEditForm: React.FC<OrderItemEditFormProps> = ({
  order,
  orderItem,
  selectedTrip,
  serviceId,
  cabinOptions,
  individualTargetSuppliedItems,
  individualDomesticAirSuppliedItems,
  handleRemoveElement,
  updateIsAirNumbersMissing,
  hotelElementProviderOptions,
  carProviderOptions,
  carTypeOptions,
  shippingCategoryOptions,
  wifiProviderOptions,
  updateForeignAirTransportElements,
  updatePriceDetailsFromPnr,
  timeZones
}) => {
  const [pnrLoading, setPnrLoading] = useState(false);
  const [pnrLoadResult, setPnrLoadResult] = useState<{ status: '' | 'success' | 'error'; message: string }>({
    status: '',
    message: ''
  });

  const initialOrderItem = useMemo(() => {
    return selectedTrip.initialOrderItemFor(orderItem);
  }, [selectedTrip, orderItem]);

  const orderItemDiff = orderItem.diffWith(initialOrderItem);

  const classNameForModifiedField = useCallback(
    (...path: (string | number)[]): string => {
      const modified = orderItemDiff ? _.has(orderItemDiff, path) : true;
      return modified ? 'virtual-counte-price-change-form__modified_field' : '';
    },
    [orderItemDiff]
  );

  const handleAirNumbersChange = useCallback(
    (
      elementId: number | undefined,
      action: 'handleAirReservationNumberChange' | 'handleAirConfirmationNumberChange',
      value: string
    ) => {
      orderItem.elements.forEach(el => {
        if (el.type === 'transport' && el.id === elementId) {
          if (el instanceof TransportElement) {
            if (action === 'handleAirReservationNumberChange') {
              el.airReservationNumber = value;
            } else {
              el.airConfirmationNumber = value;
            }
          }
        }
      });
      updateIsAirNumbersMissing(orderItem);
    },
    [orderItem, updateIsAirNumbersMissing]
  );

  const handleIsForeignChange = useCallback(
    (element: HotelElement, address: string | undefined) => {
      if (address === '') return;

      Fetcher.get<{ is_foreign: boolean }>(`/places/is_foreign?address=${address}`).then(({ is_foreign }) => {
        element.handleIsForeignChange(is_foreign);
        const price = orderItem.price;
        if (price) {
          if (is_foreign) {
            price.taxTypeId = TAX_TYPE_ID.FUKAZEI_ID;
            price.priceDetails?.forEach(priceDetail => {
              priceDetail.taxTypeId = TAX_TYPE_ID.FUKAZEI_ID;
            });
            price.originalPrices?.forEach(originalPrice => {
              originalPrice.taxTypeId = TAX_TYPE_ID.FUKAZEI_ID;
            });
          } else {
            price.taxTypeId = TAX_TYPE_ID.TAXABLE_10_ID;
            price.priceDetails?.forEach(priceDetail => {
              priceDetail.taxTypeId = TAX_TYPE_ID.TAXABLE_10_ID;
            });
            price.originalPrices?.forEach(originalPrice => {
              originalPrice.taxTypeId = TAX_TYPE_ID.TAXABLE_10_ID;
            });
          }
        }
      });
    },
    [orderItem]
  );

  const handleIsForeignLoad = useCallback(
    (element: HotelElement) => {
      if (element.isForeign !== null && element.isForeign !== undefined) return;
      if (element.address === '') return;

      handleIsForeignChange(element, element.address);
    },
    [handleIsForeignChange]
  );

  const handleReplaceForeignAirInfoFromPnr = useCallback(
    (orderItemToUpdate: OrderItem) => {
      const cabin = orderItemToUpdate.transports
        .filter(el => el.type === 'transport' && el.isForeignAir)
        .map(el => el.cabin)
        .find(c => c !== undefined);

      const params = {
        pnr_number: orderItemToUpdate.pnrId,
        people_num: orderItemToUpdate.travelerInformations.length,
        cabin
      };

      type AmadeusPnrRetrieveResponse = {
        data: {
          result: PnrRetrieveAllResponseJson;
          time: string;
        };
      };

      setPnrLoading(true);
      setPnrLoadResult({ status: '', message: '' });

      Fetcher.post<AmadeusPnrRetrieveResponse>('/arrangement/amadeus_pnr_retrieve', params)
        .then(res => {
          const responseFlights: PnrRetrieveFlightResponseJson[] = [];
          res.data.result.flight_result.forEach((flight: PnrRetrieveFlightResponseJson) => {
            responseFlights.push(flight);
          });
          updateForeignAirTransportElements(orderItemToUpdate, responseFlights);

          const responsePrices: PnrRetrievePriceResponseJson[] = [];
          res.data.result.price_result.forEach((price: PnrRetrievePriceResponseJson) => {
            responsePrices.push(price);
          });
          updatePriceDetailsFromPnr(orderItemToUpdate, responsePrices);

          let successMessage = `${res.data.time} PNRから情報を反映に成功しました。`;
          if (responseFlights.length === 0) {
            successMessage = `${successMessage} フライト情報が見つかりませんでした。`;
          }
          if (responsePrices.length === 1 && responsePrices[0].price === 0) {
            successMessage = `${successMessage} 料金情報が見つかりませんでした。`;
          }

          setPnrLoadResult({ status: 'success', message: successMessage });
        })
        .catch(e => {
          let errorTime = '';
          let msg = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
          if (e instanceof HTTPError) {
            if (e.response?.data.error.message) {
              msg = e.response.data.error.message;
            }
            errorTime = e.response?.data.error.time;
          }

          setPnrLoadResult({
            status: 'error',
            message: `${errorTime} PNRから情報を反映に失敗しました。 ${msg}`
          });
        })
        .finally(() => {
          setPnrLoading(false);
        });
    },
    [updateForeignAirTransportElements, updatePriceDetailsFromPnr]
  );

  return orderItem.orderItemCategory === 'arrangement_request' ? (
    <div className="virtual-counte-price-change-form__element-block">
      <div className="virtual-counte-price-change-form__input-wrapper">
        <Text text="リクエストフォーム利用料" />
      </div>
    </div>
  ) : (
    <>
      {orderItem.elements.some(
        element =>
          element instanceof TransportElement &&
          (element.transportType === 'foreign_air' || element.transportType === 'domestic_air')
      ) && (
        <div style={{ display: 'flex' }}>
          <span style={{ marginRight: '5px', flexShrink: 0 }} className={classNameForModifiedField('pnrId')}>
            PNR ID
          </span>
          <input
            type="text"
            value={orderItem.pnrId || ''}
            onChange={e => {
              orderItem.pnrId = e.target.value;
            }}
          />
        </div>
      )}
      {orderItem.elements.some(
        element => element instanceof TransportElement && element.transportType === 'foreign_air'
      ) && (
        <>
          <Button
            disabled={orderItem.travelerInformations.length === 0}
            loading={pnrLoading}
            onClick={() => handleReplaceForeignAirInfoFromPnr(orderItem)}
            style={{ padding: '5px', marginLeft: '5px', minWidth: '60px' }}
          >
            PNRから情報を反映
          </Button>
          {pnrLoadResult?.status === 'success' && pnrLoadResult?.message && (
            <div style={{ color: '#28a745' }}>{pnrLoadResult.message}</div>
          )}
          {pnrLoadResult?.status === 'error' && pnrLoadResult?.message && (
            <DangerText color="danger">{pnrLoadResult.message}</DangerText>
          )}
          {orderItem.travelerInformations.length === 0 && (
            <DangerText color="danger">※利用者が設定されていません</DangerText>
          )}
          {isAiTravel(serviceId) && (
            <div style={{ display: 'flex', margin: '10px 0' }}>
              <span
                style={{ marginRight: '5px' }}
                className={classNameForModifiedField('foreignFlightTicketIssuer')}
              >
                発券先
              </span>
              <select
                value={orderItem.foreignFlightTicketIssuer}
                onChange={e => {
                  orderItem.foreignFlightTicketIssuer = e.target.value;
                }}
              >
                {ForeignFlightTicketIssuerKeys.map(key => (
                  <option key={key} value={key}>
                    {ForeignFlightTicketIssuer[key]}
                  </option>
                ))}
              </select>
            </div>
          )}
        </>
      )}
      {orderItem.elements.map((element, j) => (
        <div className="virtual-counte-price-change-form__element-block" key={`element-${element.id || j}`}>
          <div className="virtual-counte-price-change-form__input-wrapper">
            <span
              className={`virtual-counte-price-change-form__label ${classNameForModifiedField(
                'elements',
                j,
                'type'
              )}`}
            >
              タイプ
            </span>
            <select value={element.type} onChange={e => orderItem.handleChangeType(j, e.target.value)}>
              <option value="transport">交通</option>
              <option value="hotel">ホテル</option>
              <option value="cancel">キャンセル料</option>
              <option value="change_fee">変更手数料</option>
              {isMynavi(serviceId) && <option value="support_fee">出張サポート費</option>}
              <option value="shipping">配送料</option>
              <option value="wifi">WiFi</option>
              <option value="room">会議室</option>
              <option value="kit">検査キット</option>
              <option value="travel_authorization">査証・渡航認証関連</option>
              <option value="insurance">保険</option>
            </select>
            <Link
              type="button"
              style={{ marginLeft: '10px', fontSize: '18px', fontWeight: 'bold' }}
              onClick={() => handleRemoveElement(orderItem, j)}
              data-testid="remove-element-button"
            >
              ×
            </Link>
          </div>
          {element instanceof TransportElement ? (
            <TransportElementForm
              element={element}
              index={j}
              serviceId={serviceId}
              orderItem={orderItem}
              initialOrderItem={initialOrderItem ?? null}
              cabinOptions={cabinOptions}
              handleAirNumbersChange={handleAirNumbersChange}
              classNameForModifiedField={classNameForModifiedField}
              individualTargetSuppliedItems={individualTargetSuppliedItems}
              individualDomesticAirSuppliedItems={individualDomesticAirSuppliedItems}
              carProviderOptions={carProviderOptions}
              carTypeOptions={carTypeOptions}
              timeZones={timeZones}
            />
          ) : element instanceof HotelElement ? (
            <HotelElementForm
              element={element}
              orderItem={orderItem}
              providerOptions={hotelElementProviderOptions}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
              handleIsForeignChange={handleIsForeignChange}
              handleIsForeignLoad={handleIsForeignLoad}
            />
          ) : element instanceof CancelChargeElement ? (
            <CancelElementForm
              element={element}
              traceId={orderItem.traceId}
              traceItems={order.traceItemDescriptions}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof ChangeFeeElement ? (
            <ChangeFeeElementForm
              element={element}
              traceId={orderItem.traceId}
              traceItems={order.traceItemDescriptions}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof SupportFeeElement ? (
            <SupportFeeElementForm
              element={element}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof ShippingElement ? (
            <ShippingElementForm
              element={element}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
              shippingCategoryOptions={shippingCategoryOptions}
            />
          ) : element instanceof WifiElement ? (
            <WifiElementForm
              element={element}
              orderItem={orderItem}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
              wifiProviderOptions={wifiProviderOptions}
            />
          ) : element instanceof RoomElement ? (
            <RoomElementForm
              element={element}
              orderItem={orderItem}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof KitElement ? (
            <KitElementForm
              element={element}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof TravelAuthorizationElement ? (
            <TravelAuthorizationElementForm
              element={element}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : element instanceof InsuranceElement ? (
            <InsuranceElementForm
              element={element}
              classNameForModifiedField={(...path: (string | number)[]) =>
                classNameForModifiedField('elements', j, ...path)
              }
            />
          ) : null}
        </div>
      ))}
      <div className="virtual-counte-price-change-form__element-block">
        <div className="virtual-counte-price-change-form__input-wrapper">
          <div className="flex">
            <select
              value={orderItem.additionalType}
              onChange={e => {
                orderItem.additionalType = e.target.value;
              }}
            >
              <option value="transport">交通</option>
              <option value="hotel">ホテル</option>
              <option value="cancel">キャンセル料</option>
              <option value="change_fee">変更手数料</option>
              {isMynavi(serviceId) && <option value="support_fee">出張サポート費</option>}
              <option value="shipping">配送料</option>
              <option value="wifi">WiFi</option>
              <option value="room">会議室</option>
              <option value="kit">検査キット</option>
              <option value="travel_authorization">査証・渡航認証関連</option>
              <option value="insurance">保険</option>
            </select>
            <Button size="small" onClick={e => orderItem.handleAddElementByType(e)}>
              追加する
            </Button>
          </div>
        </div>
      </div>
      {orderItem.orderItemCategory === 'jr_package' && (
        <div className="virtual-counte-price-change-form__element-block">
          <div className="virtual-counte-price-change-form__input-wrapper">
            <span className={`virtual-counte-price-change-form__label ${classNameForModifiedField('jrAuthCode')}`}>
              JR認証番号
            </span>
            <input
              type="text"
              value={orderItem.jrAuthCode || ''}
              onChange={e => {
                orderItem.jrAuthCode = e.target.value;
              }}
            />
          </div>
          <div className="virtual-counte-price-change-form__input-wrapper">
            <span
              className={`virtual-counte-price-change-form__label ${classNameForModifiedField('jrPickupCode')}`}
            >
              JR引取番号
            </span>
            <input
              type="text"
              value={orderItem.jrPickupCode || ''}
              onChange={e => {
                orderItem.jrPickupCode = e.target.value;
              }}
            />
          </div>
        </div>
      )}
      {orderItem.orderItemCategory === 'domestic_air' && (
        <div className="virtual-counte-price-change-form__element-block">
          <div className="virtual-counte-price-change-form__input-wrapper">
            <span
              className={`virtual-counte-price-change-form__label ${classNameForModifiedField(
                'purchaseDeadline'
              )}`}
            >
              国内航空券の購入期限
            </span>
            <Datepicker
              value={orderItem.purchaseDeadline?.toDate() || undefined}
              onChange={(d: Date | null) => {
                orderItem.purchaseDeadline = d ? moment(d) : undefined;
              }}
              type="datetime-local"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default observer(OrderItemEditForm);
